import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { ThemeToggle } from '@/shared/components/theme-toggle';
import { Bell, CheckCheck, Inbox, Menu } from 'lucide-react';
import { useUIStore } from '@/store';
import { inAppNotificationService, type InAppNotification } from '@/features/matchup/services/inapp-notification-service';
import { BpmQrModal } from '@/features/bpm/components/bpm-qr-modal';
import { MyQrCodeModal } from '@/features/bpm/components/my-qr-code-modal';
import { GuideButton } from '@/features/gms';
import { ReportProblemButton } from '@/features/helpdesk/components/report-problem-button';
import { useQueueCounts } from '@/features/helpdesk/hooks/use-queue-counts';
import type { HelpdeskQueueCounts } from '@/features/helpdesk/services/helpdesk-service';
import './header.css';

const LOGO_URL =
  'https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/fa509ca3-1165-43d5-b075-f174c232cb04.png?alt=media&token=0f5855f4-8176-47ca-b842-6d7d1301b939';

/**
 * Where the "needs you" indicator leads: the staff queue when it has work (or is the
 * only queue), otherwise Team Support. With nothing needing action but overdue tickets,
 * it opens the "At risk" view of the queue that has them.
 */
function needsYouTarget(counts: HelpdeskQueueCounts): string {
  const staffPath = '/admin/helpdesk?view=needs_action';
  const teamPath = '/team/support?view=needs_action';
  if (counts.staff && counts.staff.needs_action > 0) return staffPath;
  if (counts.team && counts.team.needs_action > 0) return teamPath;
  if (counts.staff && counts.staff.breached > 0) return '/admin/helpdesk?view=at_risk';
  if (counts.team && counts.team.breached > 0) return '/team/support?view=at_risk';
  return counts.staff ? staffPath : teamPath;
}

/** Title/label of the "needs you" indicator, e.g. "3 tickets need you · 1 overdue". */
function needsYouLabel(needsYou: number, overdue: number): string {
  const parts: string[] = [];
  if (needsYou > 0) parts.push(`${needsYou} ticket${needsYou === 1 ? '' : 's'} need${needsYou === 1 ? 's' : ''} you`);
  if (overdue > 0) parts.push(`${overdue} overdue`);
  return parts.join(' · ');
}

export function Header() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  // BPM QR (Phase 8). Both modals live here rather than on a page because they
  // are reached from the profile menu, which is on every screen. `BpmQrModal` is
  // opened with no occurrence: from here only an event code on a screen resolves,
  // because nothing has selected a BPM date.
  const [scanOpen, setScanOpen] = useState(false);
  const [myQrOpen, setMyQrOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Helpdesk tickets waiting on this user (staff and/or Team Support), polled every 60 s.
  const { data: queueCounts } = useQueueCounts();
  const needsYouCount = queueCounts?.total_needs_action ?? 0;
  const overdueCount = queueCounts?.total_breached ?? 0;
  const notificationsRef = useRef<HTMLDivElement>(null);
  // Phones only (hidden by header.css on wider screens): opens the sidebar drawer.
  // Primitive selectors, so an unrelated UI-store change does not re-render the header.
  const mobileNavOpen = useUIStore((state) => state.mobileNavOpen);
  const toggleMobileNav = useUIStore((state) => state.toggleMobileNav);

  // Get first name
  const firstName = (user?.displayName || user?.name || user?.email || 'User').split(' ')[0];
  // Assistant mode: greet the assistant, not the SMD whose account they work in.
  const actingAs = user?.actingAs ?? null;

  // Avatar: photo URL or initials
  const avatarUrl = user?.photoURL || null;
  const initials = (() => {
    const full = user?.displayName || user?.name || '';
    const parts = full.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    return (user?.email?.[0] ?? 'U').toUpperCase();
  })();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    if (dropdownOpen || notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, notificationsOpen]);

  useEffect(() => {
    if (!user || !localStorage.getItem('wb.authToken')) return;
    const refreshCount = () => void inAppNotificationService.unreadCount().then(setUnreadCount).catch(() => undefined);
    refreshCount();
    const interval = window.setInterval(refreshCount, 60_000);
    return () => window.clearInterval(interval);
  }, [user]);

  const openNotifications = async () => {
    const nextOpen = !notificationsOpen;
    setNotificationsOpen(nextOpen);
    setDropdownOpen(false);
    if (!nextOpen) return;
    setNotificationsLoading(true);
    try {
      const data = await inAppNotificationService.unread();
      setNotifications(data.results);
      setUnreadCount(data.count);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const openNotification = async (notification: InAppNotification) => {
    await inAppNotificationService.markRead([notification.id]).catch(() => undefined);
    setNotifications((current) => current.filter((item) => item.id !== notification.id));
    setUnreadCount((current) => Math.max(0, current - 1));
    setNotificationsOpen(false);
    // Notifications that carry their own in-app link (e.g. helpdesk tickets) open it;
    // everything else is a Match Up notification and keeps the original behaviour.
    const link = notification.payload?.link;
    if (typeof link === 'string' && link.startsWith('/') && !link.startsWith('//')) {
      navigate(link);
      return;
    }
    navigate('/matchup', { state: { appointmentId: notification.payload?.appointment_id } });
  };

  const markAllNotificationsRead = async () => {
    await inAppNotificationService.markAllRead();
    setNotifications([]);
    setUnreadCount(0);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate('/settings');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setDropdownOpen(false);
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login');
    }
  };

  return (
    <header className="header">
      <button
        type="button"
        className="header__menu-button"
        onClick={toggleMobileNav}
        aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
        aria-controls="app-sidebar"
        aria-expanded={mobileNavOpen}
      >
        <Menu size={22} />
      </button>

      {/* Left: Logo */}
      <div className="header__logo-container">
        <img
          src={LOGO_URL}
          alt="Wealth Builders"
          className="header__logo"
          loading="eager"
          decoding="async"
        />
      </div>

      {/* Right: Welcome, Theme Toggle, Profile Dropdown */}
      <div className="header__right">
        <span className="header__welcome">
          {actingAs ? (
            <>
              Welcome, {actingAs.assistant_name}
              <span className="header__welcome-suffix"> for {actingAs.principal_name}</span>
            </>
          ) : (
            <>Welcome, {firstName}</>
          )}
        </span>

        <ThemeToggle />

        <GuideButton />

        <ReportProblemButton />

        {queueCounts && (needsYouCount > 0 || overdueCount > 0) ? (
          <button
            type="button"
            className="header__bell-button"
            onClick={() => navigate(needsYouTarget(queueCounts))}
            aria-label={needsYouLabel(needsYouCount, overdueCount)}
            title={needsYouLabel(needsYouCount, overdueCount)}
          >
            <Inbox size={20} />
            {needsYouCount > 0 ? (
              <span className="header__notification-badge header__needs-you-badge">{needsYouCount > 99 ? '99+' : needsYouCount}</span>
            ) : null}
            {overdueCount > 0 ? <span className="header__overdue-dot" aria-hidden="true" /> : null}
          </button>
        ) : null}

        <div ref={notificationsRef} className="header__notifications">
          <button type="button" className="header__bell-button" onClick={() => void openNotifications()} aria-label="Open notifications" title="Notifications">
            <Bell size={20} />
            {unreadCount > 0 ? <span className="header__notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
          </button>
          {notificationsOpen ? (
            <div className="header__notification-menu">
              <div className="header__notification-title"><strong>Notifications</strong>{notifications.length ? <button type="button" onClick={() => void markAllNotificationsRead()}><CheckCheck size={15} /> Mark all read</button> : null}</div>
              <div className="header__notification-list">
                {notificationsLoading ? <p>Loading notifications...</p> : notifications.length ? notifications.map((notification) => (
                  <button key={notification.id} type="button" className="header__notification-item" onClick={() => void openNotification(notification)}>
                    <span className="header__notification-dot" />
                    <span><strong>{notification.title}</strong><small>{notification.body}</small><time>{new Date(notification.created_at).toLocaleString()}</time></span>
                  </button>
                )) : <p>You're all caught up.</p>}
              </div>
            </div>
          ) : null}
        </div>

        <div ref={dropdownRef} className="header__dropdown">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={`header__avatar-button ${dropdownOpen ? 'header__avatar-button--active' : ''}`}
            title="User menu"
            aria-label="Open user menu"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt={firstName} className="header__avatar-img" />
            ) : (
              <span className="header__avatar-initials">{initials}</span>
            )}
          </button>

          {dropdownOpen && (
            <div className="header__dropdown-menu">
              {/* Header */}
              <div className="header__dropdown-header">
                <div className="header__dropdown-name">
                  {firstName}
                </div>
                <div className="header__dropdown-email">
                  {user?.email || 'user@example.com'}
                </div>
              </div>

              <div className="header__dropdown-divider" />

              {/* QR check-in. §3 asked for these "between my login and Profile";
                  there is no "my login" item — the name/email block above is it. */}
              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setScanOpen(true);
                }}
                className="header__dropdown-item"
              >
                <span>📷</span> Scan QR
              </button>

              <button
                onClick={() => {
                  setDropdownOpen(false);
                  setMyQrOpen(true);
                }}
                className="header__dropdown-item"
              >
                <span>🔳</span> My QR Code
              </button>

              <div className="header__dropdown-divider" />

              {/* Profile Item */}
              <button
                onClick={handleProfileClick}
                className="header__dropdown-item"
              >
                <span>👤</span> Profile
              </button>

              <button
                onClick={() => {
                  setDropdownOpen(false);
                  navigate('/privacy-policy');
                }}
                className="header__dropdown-item"
              >
                <span>🔒</span> Privacy Policy
              </button>

              <div className="header__dropdown-divider" />

              {/* Sign Out Item */}
              <button
                onClick={handleLogout}
                className="header__dropdown-item header__dropdown-item--danger"
              >
                <span>🚪</span> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      <BpmQrModal open={scanOpen} onClose={() => setScanOpen(false)} />
      <MyQrCodeModal open={myQrOpen} onClose={() => setMyQrOpen(false)} />
    </header>
  );
}
